import { useEffect, useRef, useCallback } from 'react';
import { isTDOT, SUPERBAR_ROOT_SELECTOR } from '../constants/stores';

// TODO: make this dynamic, for now only TDOT is supported
const conflictingElementSelector = '.vehicle-navigation-content-wrapper';
const stickyTriggerSelector = '.vehicle-navigator-form-title-sticky-button';

interface SuperbarPositionConfig {
  isAvailableToCurrentUser: boolean;
  storeId?: string;
}

/**
 * React hook that dynamically positions the superbar (#caddie-root),
 * ensuring it does not overlap with other fixed elements (such as a navigation wrapper or sticky button) on the page.
 *
 * - Observes intersection and size changes of relevant elements to adjust the superbar's bottom offset.
 * - If a conflicting navigation element is present and a sticky trigger is intersecting, the superbar is moved up
 *   by the height and bottom of the navigation element.
 * - If not, the superbar is fixed to the bottom of the viewport.
 * - Also observes class and DOM mutations to keep the position in sync with UI changes.
 *
 * @param isAvailableToCurrentUser - Whether the superbar should be available for the current user.
 * @param storeId - Optional store identifier to conditionally enable the observer.
 */
export const useSuperbarPosition = ({
  isAvailableToCurrentUser,
  storeId,
}: SuperbarPositionConfig) => {
  const intersectingRef = useRef(false);

  const checkPosition = useCallback((isIntersecting: boolean) => {
    const superbar: HTMLElement | null = document.querySelector(
      SUPERBAR_ROOT_SELECTOR
    );
    const conflictingElement: HTMLElement | null = document.querySelector(
      conflictingElementSelector
    );
    const stickyTrigger: HTMLElement | null = stickyTriggerSelector
      ? document.querySelector(stickyTriggerSelector)
      : null;

    if (!superbar || !conflictingElement) return;

    // Check if superbar is in gallery mode
    if (superbar.classList.contains('caddie-root-showing-gallery')) {
      superbar.style.cssText += 'bottom: 0px !important;';
      return;
    }

    // If no sticky trigger or not intersecting, reset to bottom 0
    if (!stickyTrigger || !isIntersecting) {
      superbar.style.cssText += 'bottom: 0px !important;';
    } else {
      // Calculate new bottom position based on conflicting element
      const elementBottom =
        parseInt(window.getComputedStyle(conflictingElement).bottom) || 0;
      const elementHeight = conflictingElement.offsetHeight;
      const newBottom = elementBottom + elementHeight;

      superbar.style.cssText += `bottom: ${newBottom}px !important;`;
    }
  }, []);

  useEffect(() => {
    if (!isAvailableToCurrentUser || !isTDOT(storeId)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (intersectingRef.current !== entry.isIntersecting) {
            checkPosition(entry.isIntersecting);
          }
          intersectingRef.current = entry.isIntersecting;
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      }
    );

    const resizeObserver = new ResizeObserver(() => {
      checkPosition(intersectingRef.current);
    });

    const startObserving = () => {
      const stickyTrigger = stickyTriggerSelector
        ? document.querySelector(stickyTriggerSelector)
        : null;
      const conflictingElement = document.querySelector(
        conflictingElementSelector
      );

      if (stickyTrigger) {
        observer.observe(stickyTrigger);
      } else {
        checkPosition(false);
      }

      if (conflictingElement) {
        resizeObserver.observe(conflictingElement);
      }
    };

    // Setup mutation observer for superbar class changes
    const superbar = document.querySelector(SUPERBAR_ROOT_SELECTOR);
    const superbarClassObserver = new MutationObserver(() => {
      checkPosition(intersectingRef.current);
    });

    if (superbar) {
      superbarClassObserver.observe(superbar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // Start initial observation
    startObserving();

    // Setup observer for dynamic element addition/removal
    const mutationObserver = new MutationObserver(() => {
      const stickyTrigger = stickyTriggerSelector
        ? document.querySelector(stickyTriggerSelector)
        : null;

      if (stickyTrigger && !observer.takeRecords().length) {
        startObserving();
      } else if (!stickyTrigger) {
        checkPosition(false);
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      superbarClassObserver.disconnect();
    };
  }, [isAvailableToCurrentUser, storeId, checkPosition]);
};
